import React from 'react';
import numbers from '~/utils/numbers';
import treatNotes from './treatNotes';
import InfoTooltip from './InfoTooltip';
import { tooltipTexts } from './tooltipTexts';

import './style.css';

interface Props {
    data: any;
    calc: any;
}

const CativeBox: React.FC<Props> = (props) => {
    const data = props.data ?? {};
    const calc = props.calc ?? {};

    const safeDemands = data?.demands ?? {
        consumed: { peak: 0, offPeak: 0 },
        notConsumed: { peak: 0, offPeak: 0 },
        overtaking: { peak: 0, offPeak: 0 },
    };
    const safeOthers = data?.others ?? { cative: 0, freeMarket: 0 };
    const safeData = {
        ...data,
        modality: data?.modality ?? 'Verde',
        demands: safeDemands,
        others: safeOthers,
        consumptionPeak: data?.consumptionPeak ?? 0,
        consumptionOffPeak: data?.consumptionOffPeak ?? 0,
        consumptionPeakTE: data?.consumptionPeakTE ?? 0,
        consumptionOffPeakTE: data?.consumptionOffPeakTE ?? 0,
        pisCofins: data?.pisCofins ?? 0,
        icms: data?.icms ?? 0,
    };

    const safeFlagInfos = calc?.flagInfos ?? { name: '-', value: 0 };
    const safeCalc = { ...calc, flagInfos: safeFlagInfos };

    // Init treat notes
    try {
        treatNotes.init(safeData, safeCalc);
    } catch (e) {
        console.log('treatNotes.init failed (Cative):', e);
    }

    return (
        <div className="cative">
            <h2>Custo do Mercado Cativo</h2>

            <div className="body">
                <div className="line title">
                    <div className="label"></div>
                    <div className="qtty">Quant.</div>
                    <div className="mult">Tarifas</div>
                    <div className="value">Valor</div>
                </div>

                {
                    safeData.modality==='Verde' ? (
                        <>
                            <div className="line">
                                <div className="label">
                                    Demanda Única (KW)
                                    <InfoTooltip text={tooltipTexts.demandaUnica} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.consumed.offPeak) }</div>
                                <div className="mult">{ numbers.format(safeCalc.demandUniqueMult, true, 5) }</div>
                                <div className="value">{ numbers.money(safeCalc.demandUnique, false) }</div>
                            </div>
                            { safeData.demands.notConsumed.offPeak!==0 &&
                                <div className="line">
                                    <div className="label">
                                        Demanda Única Sem ICMS (KW)
                                        <InfoTooltip text={tooltipTexts.demandaSemIcms} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.notConsumed.offPeak) }</div>
                                    <div className="mult">{ numbers.format(safeCalc.demandUniqueMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(safeCalc.demandNoIcmsUnique, false) }</div>
                                </div>
                            }

                            { safeData.demands.overtaking.offPeak!==0 &&
                                <div className="line">
                                    <div className="label">
                                        Ultrapassagem de demanda única (KW)
                                        <InfoTooltip text={tooltipTexts.ultrapassagemDemanda} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.overtaking.offPeak) }</div>
                                    <div className="mult">{ numbers.format(safeCalc.demandOvertakingUniqueMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(safeCalc.demandOvertakingUnique, false) }</div>
                                </div>
                            }
                        </>
                    ) : (
                        <>
                            <div className="line">
                                <div className="label">
                                    Demanda Ponta (KW)
                                    <InfoTooltip text={tooltipTexts.demandaPonta} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.consumed.peak) }</div>
                                <div className="mult">{ numbers.format(safeCalc.demandPeakMult, true, 5) }</div>
                                <div className="value">{ numbers.money(safeCalc.demandPeak, false) }</div>
                            </div>

                            <div className="line">
                                <div className="label">
                                    Demanda Fora Ponta (KW)
                                    <InfoTooltip text={tooltipTexts.demandaForaPonta} />
                                </div>
                                <div className="qtty">{ numbers.format(safeData.demands.consumed.offPeak) }</div>
                                <div className="mult">{ numbers.format(safeCalc.demandOffPeakMult, true, 5) }</div>
                                <div className="value">{ numbers.money(safeCalc.demandOffPeak, false) }</div>
                            </div>

                            { safeCalc.demandPeakNoIcms !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Demanda Ponta Sem ICMS (KW)
                                        <InfoTooltip text={tooltipTexts.demandaSemIcms} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.notConsumed.peak) }</div>
                                    <div className="mult">{ numbers.format(safeCalc.demandPeakMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(safeCalc.demandPeakNoICMS, false) }</div>
                                </div>
                            }

                            { safeCalc.demandOffPeakNoIcms !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Demanda Fora Ponta Sem ICMS (KW)
                                        <InfoTooltip text={tooltipTexts.demandaSemIcms} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.notConsumed.offPeak) }</div>
                                    <div className="mult">{ numbers.format(safeCalc.demandOffPeakMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(safeCalc.demandOffPeakNoICMS, false) }</div>
                                </div>
                            }

                            { safeData.demands.overtaking.peak !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Ultrapassagem de Demanda Ponta (KW)
                                        <InfoTooltip text={tooltipTexts.ultrapassagemDemanda} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.overtaking.peak) }</div>
                                    <div className="mult">{ numbers.format(safeCalc.demandOvertakingPeakMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(safeCalc.demandOvertakingPeak, false) }</div>
                                </div>
                            }

                            { safeData.demands.overtaking.offPeak !== 0 &&
                                <div className="line">
                                    <div className="label">
                                        Ultrapassagem de Demanda Fora Ponta (KW)
                                        <InfoTooltip text={tooltipTexts.ultrapassagemDemanda} />
                                    </div>
                                    <div className="qtty">{ numbers.format(safeData.demands.overtaking.offPeak) }</div>
                                    <div className="mult">{ numbers.format(safeCalc.demandOvertakingOffPeakMult, true, 5) }</div>
                                    <div className="value">{ numbers.money(safeCalc.demandOvertakingOffPeak, false) }</div>
                                </div>
                            }
                        </>
                    )
                }

                <div className="line">
                    <div className="label">
                        TUSD Encargos Ponta (kWh)
                        <InfoTooltip text={tooltipTexts.tusdPonta} />
                    </div>
                    <div className="qtty">{ numbers.format(safeData.consumptionPeak) }</div>
                    <div className="mult">{ numbers.format(safeCalc.consumptionPeakMult, true, 5) }</div>
                    <div className="value">{ numbers.money(safeCalc.consumptionPeak, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        TUSD Encargos Fora Ponta (kWh)
                        <InfoTooltip text={tooltipTexts.tusdForaPonta} />
                    </div>
                    <div className="qtty">{ numbers.format(safeData.consumptionOffPeak) }</div>
                    <div className="mult">{ numbers.format(safeCalc.consumptionOffPeakMult, true, 5) }</div>
                    <div className="value">{ numbers.money(safeCalc.consumptionOffPeak, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        TE Ponta (kWh)
                        <InfoTooltip text={tooltipTexts.tePonta} />
                    </div>
                    <div className="qtty">{ numbers.format(safeData.consumptionPeakTE) }</div>
                    <div className="mult">{ numbers.format(safeCalc.tePeakMult, true, 5) }</div>
                    <div className="value">{ numbers.money(safeCalc.tePeak, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        TE Fora Ponta (kWh)
                        <InfoTooltip text={tooltipTexts.teForaPonta} />
                    </div>
                    <div className="qtty">{ numbers.format(safeData.consumptionOffPeakTE) }</div>
                    <div className="mult">{ numbers.format(safeCalc.teOffPeakMult, true, 5) }</div>
                    <div className="value">{ numbers.money(safeCalc.teOffPeak, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        Base de Cálculo ICMS (R$)
                        <InfoTooltip text={tooltipTexts.baseCalculoIcms} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(safeCalc.calcIcms, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        Base de Cálculo PIS/COFINS (R$)
                        <InfoTooltip text={tooltipTexts.baseCalculoPisCofins} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(safeCalc.calcPisCofins, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        PIS/COFINS
                        <InfoTooltip text={tooltipTexts.pisCofins} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult">{ numbers.format(safeData.pisCofins, true, 2) }%</div>
                    <div className="value">{ numbers.money(safeCalc.pisCofins, false) }</div>
                </div>

                <div className="line">
                    <div className="label">
                        ICMS
                        <InfoTooltip text={tooltipTexts.icms} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult">{ numbers.format(safeData.icms, true, 2) }%</div>
                    <div className="value">{ numbers.money(safeCalc.icms, false) }</div>
                </div>

                {
                    safeCalc.feeInterestCorrection !== 0 &&
                    <div className="line">
                        <div className="label">
                            Multa, Juros e Correção (R$)
                            <InfoTooltip text={tooltipTexts.multaJurosCorrecao} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value">{ numbers.money(safeCalc.feeInterestCorrection, false) }</div>
                    </div>
                }

                {
                    safeCalc.discounts !== 0 &&
                    <div className="line">
                        <div className="label">
                            Descontos (R$)
                            <InfoTooltip text={tooltipTexts.descontos} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value"> -{ numbers.money(safeCalc.discounts, false) }</div>
                    </div>
                }

                { safeData.others.cative !== 0 &&
                    <div className="line">
                        <div className="label">
                            Outros Valores (R$)
                            <InfoTooltip text={tooltipTexts.outrosValores} />
                        </div>
                        <div className="qtty"></div>
                        <div className="mult"></div>
                        <div className="value">{ numbers.money(safeData.others.cative, false) }</div>
                    </div>
                }

                <div className="line total">
                    <div className="label">
                        Total
                        <InfoTooltip text={tooltipTexts.total} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(safeCalc.total, false) }</div>
                </div>

                <div className="line flag">
                    <div className="label">
                        Bandeira Tarifária ({ safeCalc.flagInfos?.name || '-' }) - Acréscimo (R$/KWh): { numbers.money(safeCalc.flagInfos?.value ?? 0, false) }
                        <InfoTooltip text={tooltipTexts.bandeiraTarifaria} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(safeCalc.flag, false) }</div>
                </div>

                <div className="line total end">
                    <div className="label">
                        Total com Bandeira
                        <InfoTooltip text={tooltipTexts.totalComBandeira} />
                    </div>
                    <div className="qtty"></div>
                    <div className="mult"></div>
                    <div className="value">{ numbers.money(safeCalc.totalFlag, false) }</div>
                </div>
            </div>
        </div>
    );
}

export default CativeBox;